import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStopwatch, faClipboardList, faTruckArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { useAlert } from '../../providers/AlertProvider';
import logger from '../../utils/logger';

/**
 * UnallocatedTable — displays routes that have no driver assigned (profile_id === null).
 * All existing per-cell click actions (demurrage, delivery details, job removal, etc.)
 * are preserved via the same callback props used in the old RouteContainer.
 */
const UnallocatedTable = ({
    routes,
    onDrop,
    onDragOver,
    drivers = [],
    onAssignDriver,
    isUserOnHoliday = () => false,
    theme,
    isLocked,
    showCostings,
    completedRouteUuids,
    onViewCompletion,
    deliveryAlerts,
    showJobComments,
    showJobCostings,
    showStartTime,
    showDemurrage,
    showDeliveryDetails,
    showAddFromDropdown,
    toggleConfirmed,
    contractRoute,
    editSubcontract,
    canEdit,
    onPodOff,
    deleteRoute,
    moveRoute,
    removeVehicle,
    vehicleServiceList,
    vehicleOverdueList,
    vorList,
    showCustomerConsignment,
    showNetworkFreightMetrics,
}) => {
    const { showAlert } = useAlert();
    const [moveUuid, setMoveUuid] = useState(null);
    const [moveDate, setMoveDate] = useState('');
    const [assigningRoutes, setAssigningRoutes] = useState(new Set());
    const handleSelectDriver = async (route, driverId) => {
        if (!driverId || isLocked || !canEdit || assigningRoutes.has(route.route_uuid)) return;
        const driver = drivers.find(candidate => String(candidate.id) === driverId);
        if (!driver || isUserOnHoliday(driver.id)) {
            showAlert({ message: 'This driver is unavailable for assignment.', type: 'warning', buttons: [{ label: 'Close' }] });
            return;
        }
        setAssigningRoutes(previous => new Set(previous).add(route.route_uuid));
        try {
            await onAssignDriver(driver, route.route_uuid);
        } catch (error) {
            logger.error('Driver dropdown assignment failed:', error);
            showAlert({ message: 'Could not assign the driver. Please retry.', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setAssigningRoutes(previous => {
                const next = new Set(previous);
                next.delete(route.route_uuid);
                return next;
            });
        }
    };
    const vans = ['FE24_AYJ', 'FE24_AYK'];

    const handleToggleConfirmed = (route_uuid, confirmed, is_subbed) => {
        showAlert({
            message: 'Change Status?',
            type: 'success',
            buttons: is_subbed ? [
                { type: 'primary',   label: 'Unsub',  onClick: () => contractRoute(route_uuid, false) },
                { type: 'warning',   label: 'Edit',   onClick: () => editSubcontract(route_uuid) },
                ...(canEdit ? [{ type: 'info', label: 'Pod', onClick: () => onPodOff?.(route_uuid) }] : []),
                { type: 'secondary', label: 'Cancel' },
            ] : [
                { type: 'primary',   label: 'Sub',    onClick: () => contractRoute(route_uuid, true) },
                ...(canEdit ? [{ type: 'info', label: 'Pod', onClick: () => onPodOff?.(route_uuid) }] : []),
                { type: 'secondary', label: 'Cancel' },
            ],
        });
    };

    const formatTimeToHHMM = (t) => {
        if (!t) return null;
        return t.length <= 5 ? t : t.substring(0, 5);
    };

    if (!routes || routes.length === 0) return null;

    const headerBg    = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    const headerColor = theme === 'dark' ? '#ddd' : '#333';
    const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)';

    return (
        <div style={{ marginBottom: 18 }}>
            {/* Move-to-date overlay */}
            {moveUuid && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setMoveUuid(null)}
                >
                    <div style={{ background: theme === 'dark' ? '#222' : '#fff', borderRadius: 10, padding: 24, minWidth: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4 style={{ margin: '0 0 16px', color: theme === 'dark' ? '#fff' : '#222' }}>Move job to date</h4>
                        <input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)}
                            style={{ display: 'block', width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginBottom: 16, fontSize: 15 }}
                        />
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => setMoveUuid(null)} style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer', color: theme === 'dark' ? '#ccc' : '#555' }}>Cancel</button>
                            <button onClick={() => { if (moveDate) { moveRoute(moveUuid, moveDate); setMoveUuid(null); setMoveDate(''); } }} disabled={!moveDate} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: moveDate ? '#6366f1' : '#aaa', color: '#fff', cursor: moveDate ? 'pointer' : 'not-allowed' }}>Move</button>
                        </div>
                    </div>
                </div>
            )}
            <div id={theme} className={`route-container ${theme}`} style={{ display: 'block', padding: '10px', overflowX: 'auto' }}>
                <div style={{
                    fontWeight: 700,
                    fontSize: 14,
                    padding: '7px 14px',
                    color: headerColor,
                    background: headerBg,
                    border: `2px solid ${borderColor}`,
                    borderBottom: `1px solid ${borderColor}`,
                    borderRadius: '6px 6px 0 0',
                    userSelect: 'none',
                }}>
                    Unallocated — {routes.length} job{routes.length !== 1 ? 's' : ''}
                </div>
                <table className="route-table" style={{ border: `2px solid ${borderColor}`, borderTop: 'none', borderRadius: '0 0 6px 6px' }}>
                    <thead>
                        <tr>
                            <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                                <FontAwesomeIcon style={{ width: 24, color: 'rgba(7,192,1,0.95)' }} icon={faCheck} />
                            </th>
                            <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                                <FontAwesomeIcon style={{ width: 24, color: 'rgba(255,0,0,1)' }} icon={faStopwatch} />
                            </th>
                            <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                                <FontAwesomeIcon style={{ width: 24, color: 'rgba(26,157,215,0.95)' }} icon={faClipboardList} />
                            </th>
                            <th style={{ background: 'rgba(0,0,0,0.3)' }}>Customer</th>
                            {showCustomerConsignment && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Customer Ref</th>}
                            {showNetworkFreightMetrics && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Pallets</th>}
                            {showNetworkFreightMetrics && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Weight</th>}
                            {showNetworkFreightMetrics && showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>ADR</th>}
                            <th style={{ background: 'rgba(0,0,0,0.3)' }}>Route</th>
                            <th style={{ background: 'rgba(0,0,0,0.3)' }}>Job Comments</th>
                            <th className="unallocated-assignment-column" style={{ background: 'rgba(0,0,0,0.3)' }}>Driver</th>
                            <th className="unallocated-assignment-column" style={{ background: 'rgba(0,0,0,0.3)' }}>Vehicle</th>
                            <th className="unallocated-assignment-column" style={{ background: 'rgba(0,0,0,0.3)' }}>Trailer</th>
                            {showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Start</th>}
                            {showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Consignment</th>}
                            {showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Costings</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {routes.map((route, index) => {
                            const {
                                job, route_uuid, truck_id, truck_reg, truck_type,
                                trailer_id, trailer_reg, trailer_type, external_trailer,
                                job_comments, route: routeDetails, route_ref,
                                completed, confirmed, is_subbed, demurrage, demurrage_cost,
                                costings, start_time, customer_company, customer_consignment_number,
                                pallet_count, weight, is_network_freight, adr_value,
                            } = route;

                            const rowBg = completedRouteUuids?.[route_uuid]
                                ? index % 2 === 0 ? 'rgba(26,157,215,0.35)' : 'rgba(56,189,248,0.28)'
                                : theme === 'light'
                                    ? index % 2 === 0 ? '#f9f9f9' : '#eaeaea'
                                    : index % 2 === 0 ? '#3a3a3a' : '#2e2e2e';

                            const rowColor = completedRouteUuids?.[route_uuid]
                                ? theme === 'dark' ? '#f8f9fa' : '#333'
                                : theme === 'light' ? '#333' : '#ddd';

                            return (
                                <tr key={route_uuid}
                                    data-driver-drop-route={!isLocked && canEdit ? route_uuid : undefined}
                                    className={`route-row ${completed ? 'completed' : ''} ${showNetworkFreightMetrics && is_network_freight && adr_value ? 'network-adr-row' : ''}`}
                                    style={{ background: rowBg, color: rowColor }}
                                    onDragOver={!isLocked ? onDragOver : undefined}
                                    onDrop={!isLocked ? (e) => onDrop(e, route, index) : undefined}
                                >
                                    {/* Status / confirmed */}
                                    <td className="fixed-th" style={{ width: 40, cursor: completedRouteUuids?.[route_uuid] ? 'pointer' : isLocked ? 'not-allowed' : 'crosshair' }}
                                        onClick={() => {
                                            if (completedRouteUuids?.[route_uuid]) {
                                                onViewCompletion(route_uuid, { truck_reg, trailer_reg, job_comments, route_ref });
                                            } else if (!isLocked) {
                                                handleToggleConfirmed(route_uuid, confirmed, is_subbed);
                                            }
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            style={{
                                                width: 24,
                                                color: completedRouteUuids?.[route_uuid]
                                                    ? theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
                                                    : confirmed || is_subbed
                                                    ? 'rgba(0,0,0,0.9)'
                                                    : theme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                            }}
                                            icon={is_subbed ? faTruckArrowRight : faEye}
                                        />
                                    </td>

                                    {/* Demurrage */}
                                    <td className="fixed-th" style={{ width: 40, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                        title={demurrage ? `${demurrage}: £${demurrage_cost}` : 'Add Demurrage'}
                                        onClick={() => { if (!isLocked) showDemurrage(route_uuid); }}
                                    >
                                        <FontAwesomeIcon style={{ width: 24, color: demurrage ? '#ff0000' : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} icon={faStopwatch} />
                                    </td>

                                    {/* Delivery details */}
                                    <td className="fixed-th" style={{ width: 40, cursor: 'pointer' }}
                                        title="View / Edit Delivery Details"
                                        onClick={() => showDeliveryDetails(route_uuid)}
                                    >
                                        <FontAwesomeIcon icon={faClipboardList} style={{ width: 24, color: deliveryAlerts?.has(route_uuid) ? 'rgba(220,53,69,0.95)' : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }} />
                                    </td>

                                    {/* Customer — route actions */}
                                    <td className={!isLocked ? 'cell-hoverable' : ''}>
                                        <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                            onClick={() => {
                                                if (!isLocked) {
                                                    showAlert({
                                                        message: 'What do you want to do with this job?',
                                                        type: 'warning',
                                                        buttons: [
                                                            { label: 'Delete', type: 'danger', onClick: () => deleteRoute(route_uuid) },
                                                            { label: 'Move', type: 'primary', onClick: () => { setMoveDate(''); setMoveUuid(route_uuid); } },
                                                            { label: 'Cancel', type: 'secondary' },
                                                        ],
                                                    });
                                                }
                                            }}
                                        >{customer_company || '—'}</span>
                                    </td>

                                    {showCustomerConsignment && <td>{customer_consignment_number || '----'}</td>}
                                    {showNetworkFreightMetrics && <td>{pallet_count ?? '----'}</td>}
                                    {showNetworkFreightMetrics && <td>{weight == null ? '----' : `${Number(weight).toLocaleString('en-GB', { maximumFractionDigits: 2 })} kg`}</td>}
                                    {showNetworkFreightMetrics && showCostings && <td>{is_network_freight ? adr_value || '----' : '----'}</td>}

                                    {/* Route */}
                                    <td>{routeDetails || 'No route details'}</td>

                                    {/* Job Comments */}
                                    <td className={!isLocked ? 'cell-hoverable' : ''} style={{ maxWidth: 600, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                        <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                            onClick={() => { if (!isLocked) showJobComments(route_uuid, job_comments); }}
                                        >{job_comments || <span style={{ opacity: 0.35 }}>----</span>}</span>
                                    </td>

                                    <td className="unallocated-assignment-column">
                                        <select
                                            className={`form-select form-select-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                            style={{ width: '100%', maxWidth: 170, fontSize: 12 }}
                                            aria-label={`Assign driver to ${customer_company || job || route_uuid}`}
                                            value=""
                                            disabled={isLocked || !canEdit || !onAssignDriver || assigningRoutes.has(route_uuid)}
                                            onChange={(event) => handleSelectDriver(route, event.target.value)}
                                        >
                                            <option value="">{assigningRoutes.has(route_uuid) ? 'Assigning...' : 'Select driver...'}</option>
                                            {drivers.map(driver => (
                                                <option key={driver.id} value={driver.id} disabled={isUserOnHoliday(driver.id)}>
                                                    {driver.name} {driver.surname}{isUserOnHoliday(driver.id) ? ' (on holiday)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </td>

                                    {/* Vehicle */}
                                    {truck_reg ? (
                                        <td className={`unallocated-assignment-column ${(!isLocked && !is_subbed) ? 'cell-hoverable' : ''}`}
                                            style={{ backgroundColor: vehicleServiceList.includes(truck_reg) ? 'rgba(253,191,58,0.6)' : vehicleOverdueList.includes(truck_reg) ? 'rgba(253,69,58,0.6)' : 'none' }}
                                        >
                                            <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                                onClick={() => { if (!isLocked && !is_subbed) removeVehicle(route_uuid, 'truck'); }}
                                            >
                                                {truck_reg}{vorList.includes(truck_reg) && <strong> (VOR)</strong>}
                                            </span>
                                        </td>
                                    ) : (
                                        <td className="unallocated-assignment-column cell-hoverable">
                                            <span style={{ cursor: 'pointer' }}
                                                onClick={() => { if (!isLocked && !is_subbed) showAddFromDropdown(route_uuid, 'truck'); }}
                                            >----</span>
                                        </td>
                                    )}

                                    {/* Trailer */}
                                    {trailer_reg ? (
                                        <td className={`unallocated-assignment-column ${!isLocked ? 'cell-hoverable' : ''}`}
                                            style={{ backgroundColor: vehicleServiceList.includes(trailer_reg) ? 'rgba(253,191,58,0.6)' : vehicleOverdueList.includes(trailer_reg) ? 'rgba(253,69,58,0.6)' : 'none' }}
                                        >
                                            <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                                onClick={() => { if (!isLocked) removeVehicle(route_uuid, 'trailer'); }}
                                            >{trailer_id === 1 ? external_trailer : trailer_reg}</span>
                                        </td>
                                    ) : (
                                        <td className="unallocated-assignment-column cell-hoverable">
                                            <span style={{ cursor: 'pointer' }}
                                                onClick={() => { if (!isLocked && !is_subbed) showAddFromDropdown(route_uuid, 'trailer'); }}
                                            >----</span>
                                        </td>
                                    )}

                                    {showCostings && (
                                        <td className="cell-hoverable">
                                            <span style={{ cursor: 'pointer' }}
                                                onClick={() => showStartTime(route_uuid, start_time)}
                                            >{formatTimeToHHMM(start_time) || '----'}</span>
                                        </td>
                                    )}
                                    {showCostings && <td>{route_ref || '----'}</td>}
                                    {showCostings && (
                                        <td className="cell-hoverable">
                                            <span style={{ cursor: 'pointer' }}
                                                onClick={() => showJobCostings(route_uuid, costings)}
                                            >{costings ? '£' + costings : '----'}</span>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UnallocatedTable;
